/* eslint-disable curly */
import { Excalidraw, MainMenu } from "@excalidraw/excalidraw";
import { languages, setLanguage, t } from "@excalidraw/excalidraw/i18n";
import { useEffect, useState } from "react";

import { getPreferredLanguage } from "../app-language/language-detector";

import { libraryApi, type SharedScene } from "../data/libraryApi";

import "./SharePublicViewer.scss";

interface Props {
  token: string;
}

export const SharePublicViewer = ({ token }: Props) => {
  const [data, setData] = useState<SharedScene | null>(null);
  const [error, setError] = useState<string | null>(null);
  const langCode = getPreferredLanguage();

  // The viewer is a standalone page (no main app), so localize it explicitly:
  // setLanguage feeds both the Excalidraw UI and our header's module-level t().
  useEffect(() => {
    const lang = languages.find((l) => l.code === langCode);
    if (lang) {
      setLanguage(lang);
    }
  }, [langCode]);

  useEffect(() => {
    let cancelled = false;
    libraryApi
      .getShare(token)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (error) {
    return (
      <div className="share-viewer share-viewer--error">
        <h1>{t("shareViewer.cannotLoad")}</h1>
        <p>{error}</p>
        <a href="/">{t("shareViewer.goToApp")}</a>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="share-viewer share-viewer--loading">
        {t("shareViewer.loading")}
      </div>
    );
  }

  const scene = data.scene;

  return (
    <div className="share-viewer">
      <div className="share-viewer__header">
        <div className="share-viewer__heading">
          <span className="share-viewer__title">
            {data.name || t("shareViewer.untitled")}
          </span>
          {data.description && (
            <span className="share-viewer__description">
              {data.description}
            </span>
          )}
        </div>
        <span className="share-viewer__meta">
          {data.view_count === 1
            ? t("shareViewer.metaOne", { count: data.view_count })
            : t("shareViewer.metaOther", { count: data.view_count })}
        </span>
      </div>
      <div className="share-viewer__canvas">
        <Excalidraw
          viewModeEnabled
          zenModeEnabled
          langCode={langCode}
          initialData={{
            elements: scene.elements as never,
            appState: {
              ...(scene.appState as Record<string, unknown>),
              viewModeEnabled: true,
            } as never,
            files: scene.files as never,
          }}
        >
          {/* Minimal menu: only save/export. No GitHub/Discord/socials. */}
          <MainMenu>
            <MainMenu.DefaultItems.SaveAsImage />
            <MainMenu.DefaultItems.Export />
          </MainMenu>
        </Excalidraw>
      </div>
    </div>
  );
};
